import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class SentryHttpInterceptorService implements HttpInterceptor {
  
  private excludedUrls: string[] = [
    'maps.googleapis.com',
    'api.mapbox.com',
    'sentry.io'
  ];

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    
    //no monitorear url externas
    const isExternalUrl = this.excludedUrls.some(url => request.url.includes(url));
    if (isExternalUrl) {
      return next.handle(request);
    }

    const startTime = Date.now();
    const endpointName = this.getEndpointName(request.url);

    // registrar pasos al inicio
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${request.method} ${endpointName}`,
      level: 'info',
      data: {
        url: request.url,
        method: request.method,
        headers: this.sanitizeHeaders(request.headers),
        payload: this.sanitizePayload(request.body),
      },
    });

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const duration = Date.now() - startTime;

          //checa el isSuccess
          // if (event.body && typeof event.body === 'object' && 'isSuccess' in event.body) {
          //   if (event.body.isSuccess === false) {
          //     throw new HttpErrorResponse({
          //       error: event.body,
          //       headers: event.headers,
          //       status: event.status,
          //       statusText: event.body.message || 'isSuccess: false',
          //       url: event.url || undefined
          //     });
          //   }
          // }
          
          //registrar breadcrumb para requests lentas (8s) o con errores de status
          if (duration > 8000 || event.status >= 400) {
            Sentry.addBreadcrumb({
              category: 'http.response',
              message: `${request.method} ${endpointName} (${event.status})`,
              level: event.status >= 400 ? 'warning' : 'info',
              data: {
                url: request.url,
                method: request.method,
                headers: this.sanitizeHeaders(request.headers),
                payload: this.sanitizePayload(request.body),
                status: event.status,
                duration: `${duration}ms`,
              },
            });
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const duration = Date.now() - startTime;
        Sentry.captureException(error, {
          tags: {
            'http.method': request.method,
            'http.status_code': error.status.toString(),
            'http.url': endpointName,
          },
          contexts: {
            http: {
              url: request.url,
              method: request.method,
              headers: this.sanitizeHeaders(request.headers),
              payload: this.sanitizePayload(request.body),
              status_code: error.status,
              duration: `${duration}ms`,
            },
            response: {
              body: this.sanitizeErrorBody(error.error),
              message: error.message,
            },
          },
          level: this.getErrorLevel(error.status),
        });

        //registrar breadcrumb del error
        Sentry.addBreadcrumb({
          category: 'http.error',
          message: `${request.method} ${endpointName} failed (${error.status})`,
          level: 'error',
          data: {
            url: request.url,
            method: request.method,
            headers: this.sanitizeHeaders(request.headers),
            payload: this.sanitizePayload(request.body),
            status: error.status,
            duration: `${duration}ms`,
            error: error.message,
          },
        });

        return throwError(() => error);
      })
    );
  }

  private getEndpointName(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      //devolver la parte después de /api/
      const match = url.match(/\/api\/([^?]*)/);
      return match ? `/api/${match[1]}` : url;
    }
  }

  private sanitizeErrorBody(errorBody: any): any {
    if (!errorBody) return null;

    //limita la longitud de strings para evitar enviar datos muy grandes
    if (typeof errorBody === 'string') {
      return errorBody.substring(0, 500);
    }

    //si es objeto, crear copia sin datos posiblemente malos
    if (typeof errorBody === 'object') {
      const sanitized = { ...errorBody };

      delete sanitized.password;
      delete sanitized.token;
      delete sanitized.authorization;
      delete sanitized.secret;
      return sanitized;
    }

    return errorBody;
  }

  private sanitizePayload(payload: any): any {
    if (!payload) 
      return null;

    //limita le tamaño por si está enorme
    const payloadString = JSON.stringify(payload);
    if (payloadString.length > 1000) {
      return { _note: 'El payload está muy grande', size: payloadString.length };
    }

    //checar si es objeto
    if (typeof payload === 'object') {
      const sanitized = JSON.parse(JSON.stringify(payload));
      
      //trata de eliminar campos sensibles
      const removeSensitiveData = (obj: any): any => {
        if (typeof obj !== 'object' || obj === null) return obj;
        
        const sensitiveKeys = ['password', 'token', 'authorization'];
        
        for (const key in obj) {
          if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk.toLowerCase()))) {
            obj[key] = '[CLASIFICADOOO]';
          } else if (typeof obj[key] === 'object') {
            removeSensitiveData(obj[key]);
          }
        }
        return obj;
      };
      
      return removeSensitiveData(sanitized);
    }

    return payload;
  }

  private getErrorLevel(status: number): Sentry.SeverityLevel {
    if (status >= 500) return 'error';
    if (status >= 400) return 'warning';
    return 'info';
  }

  private sanitizeHeaders(headers: any): any {
    if(headers == null || typeof headers.keys !== 'function') {
      return headers;
    }
    const headersObj: any = {};
    const sensitiveHeaders = ['password', 'token', 'authorization'];
    
    headers.keys().forEach((key: string) => {
      if (sensitiveHeaders.includes(key.toLowerCase())) {
        headersObj[key] = '[CLASIFICADOOO]';
      } else {
        headersObj[key] = headers.get(key);
      }
    });
    
    return headersObj;
  }
}
